<template>
  <v-form
    ref="relay">
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        row
        wrap>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text font-weight-light">
            {{ $t('Internal relays').toUpperCase() }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          v-for="relay in internalRelays"
          :key="'internalRelay_' + relay.relayNumber"
          xs6
          md3>
          <v-select
            v-model="relay.triggerStatus"
            :items="events"
            :label="`${ $t('Internal relay {0}'), [relay.relayNumber] }`"
            data-testId="select-Internal-relay"
            item-text="desc"
            item-value="code"
            class="dover-input"
            multiple
            outline
            dense
            @change="onInternalEventChange"/>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        v-if="hasExternalRelays"
        row
        wrap>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text font-weight-light">
            {{ $t('External relays').toUpperCase() }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          v-for="relay in externalRelays"
          :key="'externalRelay_' + relay.relayNumber"
          xs6
          md3>
          <v-select
            v-model="relay.triggerStatus"
            :items="events"
            :label="`${ $t('External relay {0}'), [relay.relayNumber] }`"
            data-testId="select-External-relay"
            item-text="desc"
            item-value="code"
            class="dover-input"
            multiple
            outline
            dense
            @change="onExternalEventChange"/>
        </v-flex>
      </v-layout>
      <v-layout
        v-else
        row
        wrap>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text font-weight-light">
            {{ $t('External relays').toUpperCase() }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12
          text-xs-center>
          <span
            class="dover-blue-color font-weight-bold">
            {{ $t('No external relays configured').toUpperCase() }}
          </span>
        </v-flex>
      </v-layout>
    </v-container>
    <v-container
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        v-if="hasOM4Relays"
        row
        wrap>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text font-weight-light">
            {{ $t('OM4 relays').toUpperCase() }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          v-for="relay in OM4Relays"
          :key="'OM4Relay_' + relay.relayNumber"
          xs6
          md3>
          <v-select
            v-model="relay.triggerStatus"
            :items="events"
            :label="`${ $t('Relay {0}'), [relay.relayNumber] }`"
            data-testId="select-Relay"
            item-text="desc"
            item-value="code"
            class="dover-input"
            multiple
            outline
            dense
            @change="onOM4EventChange"/>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
export default {
  props: {
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    relays: {
      type: Object,
      required: true,
      default: () => {}
    },
    siteInfo: {
      type: Object,
      required: true,
      default: () => {}
    },
    isPlus: {
      type: Boolean,
      required: true,
      default: () => false
    }
  },
  data: () => ({
    events: [],
    hasExternalRelays: false,
    externalRelays: [],
    internalRelays: [],
    hasOM4Relays: false,
    OM4Relays: []
  }),
  watch: {
    isPlus: {
      handler: function (newIsPlus, oldIsPlus) {
        this.filterInternalRelays()
      },
      immediate: true
    },
    options: {
      handler: function (newOptions, oldOptions) {
        if (this.events.length === 0 && newOptions !== undefined) {
          newOptions.sensorEvents.slice(1).forEach(d => { this.events.push({ code: d.code, desc: this.$t(d.desc) }) })
        }
      },
      immediate: true
    },
    siteInfo: {
      handler: function (newSiteInfo, oldSiteInfo) {
        this.filterExternalRelays()
      },
      immediate: true
    }
  },
  mounted () {
    this.filterInternalRelays()
    this.filterExternalRelays()
  },
  methods: {
    filterInternalRelays () {
      let newRelays = this.isPlus ? 2 : 4
      newRelays -= this.internalRelays.length
      for (let i = 1; i <= newRelays; i++) {
        let found = false
        for (let j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === i) && this.relays[j].internal && !this.relays[j].externalOM4) {
            this.internalRelays.push({ relayNumber: i, triggerStatus: this.unpackTriggers(this.relays[j].triggerStatus) })
            found = true
            break
          }
        }
        if (!found) {
          this.relays.push({ relayNumber: i, internal: true, externalOM4: false, triggerStatus: 0 })
          this.internalRelays.push({ relayNumber: i, triggerStatus: [] })
        }
      }
    },
    filterExternalRelays () {
      if (this.siteInfo === undefined) {
        return
      }
      this.hasExternalRelays = this.siteInfo.externalRelays > 0
      let newRelays = this.siteInfo.externalRelays - this.externalRelays.length
      for (let i = 1; i <= newRelays; i++) {
        let found = false
        for (let j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === i) && !this.relays[j].internal && !this.relays[j].externalOM4) {
            this.externalRelays.push({ relayNumber: i, triggerStatus: this.unpackTriggers(this.relays[j].triggerStatus) })
            found = true
            break
          }
        }
        if (!found) {
          this.relays.push({ relayNumber: i, internal: false, externalOM4: false, triggerStatus: 0 })
          this.externalRelays.push({ relayNumber: i, triggerStatus: [] })
        }
      }

      this.hasOM4Relays = this.siteInfo.externalOM4Relays > 0
      newRelays = this.siteInfo.externalOM4Relays - this.OM4Relays.length
      for (let i = 1; i <= newRelays; i++) {
        let found = false
        for (let j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === i) && !this.relays[j].internal && this.relays[j].externalOM4) {
            this.OM4Relays.push({ relayNumber: i, triggerStatus: this.unpackTriggers(this.relays[j].triggerStatus) })
            found = true
            break
          }
        }
        if (!found) {
          this.relays.push({ relayNumber: i, internal: false, externalOM4: true, triggerStatus: 0 })
          this.OM4Relays.push({ relayNumber: i, triggerStatus: [] })
        }
      }
    },
    onInternalEventChange () {
      for (var i = 0; i < this.internalRelays.length; i++) {
        for (var j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === this.internalRelays[i].relayNumber) && this.relays[j].internal && !this.relays[j].externalOM4) {
            this.relays[j].triggerStatus = this.packTriggers(this.internalRelays[i].triggerStatus)
            break
          }
        }
      }
      this.$emit('dataChanged', true)
    },
    onExternalEventChange () {
      for (var i = 0; i < this.externalRelays.length; i++) {
        for (var j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === this.externalRelays[i].relayNumber) && !this.relays[j].internal && !this.relays[j].externalOM4) {
            this.relays[j].triggerStatus = this.packTriggers(this.externalRelays[i].triggerStatus)
            break
          }
        }
      }
      this.$emit('dataChanged', true)
    },
    onOM4EventChange () {
      for (var i = 0; i < this.OM4Relays.length; i++) {
        for (var j = 0; j < this.relays.length; j++) {
          if ((this.relays[j].relayNumber === this.OM4Relays[i].relayNumber) && !this.relays[j].internal && this.relays[j].externalOM4) {
            this.relays[j].triggerStatus = this.packTriggers(this.OM4Relays[i].triggerStatus)
            break
          }
        }
      }
      this.$emit('dataChanged', true)
    },
    packTriggers (triggers) {
      let eventMask = 0
      for (let i = 0; i < triggers.length; i++) {
        eventMask += triggers[i]
      }
      return eventMask
    },
    unpackTriggers (eventMask) {
      let triggers = []
      let mask = 1
      for (let i = 0; i < 32; i++) {
        if ((eventMask & mask) === mask) {
          triggers.push(mask)
        }
        mask = mask * 2
      }
      return triggers
    }
  }
}
</script>
